/**
 * Bulletproof template download helper.
 *
 * The earlier axios-with-blob approach broke in two edge cases:
 *  1) When the access-token cookie had expired, backend returned 401 with a
 *     JSON error body — but axios treats the body as a Blob (because we set
 *     responseType='blob'), so the retry/refresh interceptor couldn't parse
 *     the error, silently rethrew, and the user saw the generic "Could not
 *     download template" toast.
 *  2) On Safari, the transient <a download> approach loses the click gesture
 *     when the click happens inside a Promise-then callback after the click
 *     tick has already flushed.
 *
 * The fix: use native `fetch` with `credentials: 'include'`, check
 * `response.ok` before attempting a blob download, and on 401 call the
 * shared `refreshAccessToken` from AuthContext to get a fresh cookie, then
 * retry once. On non-OK responses we decode the JSON detail from the body so
 * the toast surfaces a real message ("Session expired — please sign in again").
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function tryRefreshToken() {
  try {
    const { data } = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
    // Mirror AuthContext behavior: keep sessionStorage token in sync when the
    // preview environment blocks httpOnly cookies.
    if (data?.access_token) {
      try { sessionStorage.setItem('access_token', data.access_token); } catch (_) {}
    }
    return true;
  } catch (_) {
    return false;
  }
}

function buildHeaders() {
  const headers = {};
  try {
    const token = sessionStorage.getItem('access_token');
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch (_) { /* private mode / SSR */ }
  return headers;
}

async function fetchAsBlob(url) {
  return fetch(url, { credentials: 'include', headers: buildHeaders() });
}

/**
 * Download a file from `path` (relative to /api). Returns { ok, error? }.
 */
export async function downloadFile(path, suggestedName) {
  const url = `${API}${path}`;
  let res = await fetchAsBlob(url);

  // Auto-refresh + one retry when the access token has expired
  if (res.status === 401) {
    const refreshed = await tryRefreshToken();
    if (refreshed) res = await fetchAsBlob(url);
  }

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const text = await res.text();
      const parsed = JSON.parse(text);
      if (parsed?.detail) msg = parsed.detail;
    } catch (_) { /* keep default */ }
    return { ok: false, error: msg };
  }

  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = suggestedName || 'download';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  // Give the browser a moment to trigger the download before revoking.
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(blobUrl);
  }, 200);
  return { ok: true };
}
